import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { PlusIcon, PhoneIcon } from 'lucide-react';
import { useFlags } from '@flagsmith/flagsmith/react';

import type { ILead, IMessageAttachment, IStageHistory, ILeadPropertyInterest } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import type { IProperty, IMessage } from 'src/services/api';
import type { TourSlotSuggestion } from 'src/services/tour-availability/types';
import { ELeadStage } from 'src/services/api/leads/types';
import { ThemeProvider } from 'src/theme';
import Responsive from 'src/components/responsive/Responsive';
import { FlexColumn } from 'src/components/flex';
import LeadSidebarHeader from 'src/components/lead-sidebar/LeadSidebarHeader';
import PropertyDetails from 'src/components/lead-sidebar/LeadSidebarPropertyDetails';
import UpcomingTour from 'src/components/lead-sidebar/LeadSidebarUpcomingTour';
import LeadSidebarDayCalendar from 'src/components/lead-sidebar/LeadSidebarDayCalendar';
import PastTours from 'src/components/lead-sidebar/LeadSidebarPastTours';
import Notes from 'src/components/lead-sidebar/LeadSidebarNotes';
import ToursFromOtherLeads from 'src/components/lead-sidebar/LeadSidebarToursFromOtherLeads';
import LeadInfo from 'src/components/lead-sidebar/LeadInfo';
import SmartFields from 'src/components/lead-sidebar/SmartFields';
import AmiBandsCard from 'src/components/lead-sidebar/AmiBandsCard';
import LeadSidebarPropertyTab from 'src/components/lead-sidebar/LeadSidebarPropertyTab';
import MessageAttachments from 'src/components/lead-sidebar/LeadSidebarMessageAttachments';
import LeadStageTimeline from 'src/components/lead-sidebar/LeadStageTimeline';
import LeadSidebarListings from 'src/components/lead-sidebar/LeadSidebarListings';
import ShareLeadButton from 'src/components/lead-sidebar/ShareLeadButton';
import type { IUser } from 'src/api/users-api';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal';
import SelectCoapplicantModal from 'src/components/lead-sidebar/SelectCoapplicantModal';
import { useAssociateLeads } from 'src/services/api/leads/use-associate-leads';
import { useUpdateAssociatedLeads } from 'src/services/api/leads/use-update-associated-leads';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { LeadUtils } from 'src/utils';
import { useNotifications } from 'src/notifications';
import { EFeatureFlag } from 'src/utils/feature-flags';

import styles from 'src/components/lead-sidebar/LeadSidebar.module.css';

type BaseProps = {
    lead: ILead;
    showings: IShowing[];
    stages: IStageHistory[];
    interests: ILeadPropertyInterest[];
    teamMembers: IUser[];
    isTimelineExpanded?: boolean;
};

type LeadsPageProps = BaseProps & {
    property?: Pick<IProperty, 'id' | 'name'>;
    /**
     * Callback to close the sidebar.
     * If not provided, the close button will not be rendered
     */
    onClose: () => void;
};

type LeadMessagesPageProps = BaseProps & {
    property?: IProperty;
    messages: IMessage[];
    /** Appends text to the composer draft; enables "add to message" actions in the sidebar. */
    insertIntoReplyInput?: (text: string) => void;
    attachments?: IMessageAttachment[];
    /** The lead's live tour-slot suggestion; its slots are highlighted on the tours calendar. */
    openTourSuggestion?: TourSlotSuggestion | null;
};

const isLeadMessagesPageProps = (props: Props): props is LeadMessagesPageProps => {
    return (props as LeadMessagesPageProps).messages !== undefined;
};

type Props = LeadsPageProps | LeadMessagesPageProps;

enum SidebarTab {
    LEAD = 'lead',
    TOURS = 'tours',
    PROPERTY = 'property'
};
const TAB_VALUES = Object.values(SidebarTab);
const isTabValue = (v: string | null): v is SidebarTab => {
    if (!v) {
        return false;
    }
    return TAB_VALUES.includes(v as SidebarTab);
};

function CallLead({ phoneNumber }: { phoneNumber: string | null }) {
    return (<>
        {phoneNumber &&
            <Button
                href={`tel:${phoneNumber}`}
                startIcon={<PhoneIcon size={16} />}
                variant="outlined"
                color="primary"
                fullWidth
                data-testid="call-with-quo-btn"
            >
                Call with Quo
            </Button>
        }
    </>);
}

export default function LeadSidebar(props: Props) {
    const { lead, showings, stages, interests, teamMembers, isTimelineExpanded } = props;
    const isInSchedulingWindow =
        lead.stage === ELeadStage.CONTACTED || lead.stage === ELeadStage.TOUR_REQUESTED;
    const suggestedSlots =
        isLeadMessagesPageProps(props) && isInSchedulingWindow ?
            props.openTourSuggestion?.options ?? [] :
            [];
    const { leads, loggedInUser } = useAuthenticatedState();
    const { addNotification } = useNotifications();
    const flags = useFlags([EFeatureFlag.CALL_LEAD_WITH_QUO]);

    const [searchParams, setSearchParams] = useSearchParams();
    const rawTab = searchParams.get('sidebarTab');
    const tab = isTabValue(rawTab) ? rawTab : SidebarTab.LEAD;
    const setTab = useCallback((newTab: SidebarTab) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('sidebarTab', newTab);
            return next;
        });
    }, [setSearchParams]);

    const [isTourModalOpen, setIsTourModalOpen] = useState(false);
    const [scheduleInit, setScheduleInit] = useState<{
        startIso: string;
        propertyId: number | null;
    } | null>(null);
    // The property currently focused in the tour flow; the day calendar follows
    // it so its greyed-out touring hours match the property being booked.
    const [focusedTourPropertyId, setFocusedTourPropertyId] = useState<number | null | undefined>(
        undefined
    );
    const [isCoapplicantModalOpen, setIsCoapplicantModalOpen] = useState(false);

    const { mutateAsync: associateLeads } = useAssociateLeads();
    const { mutateAsync: updateAssociatedLeads } = useUpdateAssociatedLeads();

    const coapplicants = useMemo(() => {
        // Use the coapplicants array if it exists on the lead
        if (lead.coapplicants && Array.isArray(lead.coapplicants)) {
            return lead.coapplicants;
        }
        // Otherwise, filter from the leads list
        if (!lead.coapplicant_leads_group_id || !leads) {
            return [];
        }
        return leads.filter(
            (l) => l.coapplicant_leads_group_id === lead.coapplicant_leads_group_id && l.uuid !== lead.uuid
        );
    }, [lead.coapplicants, lead.coapplicant_leads_group_id, lead.uuid, leads]);

    const callBtn = flags.call_lead_with_quo?.enabled ? <CallLead phoneNumber={lead.phone_number} /> : null;

    return (
        <ThemeProvider>
            <Responsive desktop>
                <div>
                    <FlexColumn className={clsx(styles.root, 'prettyScrollbar')}>
                        <LeadSidebarHeader lead={lead} onClose={(props as LeadsPageProps).onClose} />

                        <FlexColumn>
                            {callBtn}

                            <PropertyDetails property={props.property} />

                            {coapplicants.length > 0 && (
                                <FlexColumn rowGap={1} mb={1}>
                                    <Typography variant="body2" fontWeight="300" color="text.secondary">
                                        Co-applicants:
                                    </Typography>
                                    <FlexColumn component="ul" className={styles.plainList}>
                                        {coapplicants.map((coapplicant) => {
                                            const leadName = LeadUtils.getLeadName(coapplicant);
                                            return (
                                                <Tooltip key={coapplicant.uuid} title={leadName}>
                                                    <Typography
                                                        component="li"
                                                        variant="body2"
                                                        fontWeight="500"
                                                        className="ellipsisText"
                                                    >
                                                        {leadName}
                                                    </Typography>
                                                </Tooltip>
                                            );
                                        })}
                                    </FlexColumn>
                                </FlexColumn>
                            )}

                            <SelectCoapplicantModal
                                open={isCoapplicantModalOpen}
                                onClose={() => setIsCoapplicantModalOpen(false)}
                                onSelect={(leadsUuids) => {
                                    if (leadsUuids.length > 0 && !leadsUuids.includes(lead.uuid)) {
                                        addNotification('Please include the current lead when selecting coapplicants.', 'error');
                                        return;
                                    }

                                    setIsCoapplicantModalOpen(false);
                                    // If editing an existing group, use the update endpoint
                                    if (lead.coapplicant_leads_group_id) {
                                        updateAssociatedLeads({
                                            group_id: lead.coapplicant_leads_group_id,
                                            lead_uuids: leadsUuids,
                                        });
                                    } else {
                                        // If adding a new group, use the associate endpoint
                                        associateLeads({
                                            lead_uuids: leadsUuids,
                                        });
                                    }
                                }}
                                currentLeadUuid={lead.uuid}
                                existingCoapplicantUuids={coapplicants.map(c => c.uuid)}
                            />

                            <Tabs
                                variant="fullWidth"
                                value={tab}
                                onChange={(_, newTab) => setTab(newTab)}
                                data-testid="lead-sidebar-tabs"
                            >
                                <Tab
                                    label="Lead"
                                    value={SidebarTab.LEAD}
                                    data-testid="general-tab"
                                />
                                <Tab
                                    label="Tours"
                                    value={SidebarTab.TOURS}
                                    data-testid="tours-tab"
                                />
                                <Tab
                                    label="Property"
                                    value={SidebarTab.PROPERTY}
                                    data-testid="property-tab"
                                />
                            </Tabs>

                            <FlexColumn className={styles.sections}>
                                {tab === SidebarTab.LEAD && (<>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setIsCoapplicantModalOpen(true)}
                                        color="primary"
                                        fullWidth
                                        disabled={lead.owner !== loggedInUser.id}
                                        data-testid="open-coapplicant-modal-btn"
                                        className={styles.openCoapplicantModalBtn}
                                        startIcon={<PlusIcon size={16} />}
                                    >
                                        {coapplicants.length > 0 ? 'Edit co-applicants' : 'Add co-applicant'}
                                    </Button>

                                    <ShareLeadButton
                                        lead={lead}
                                        tours={showings}
                                        property={props.property}
                                    />

                                    <Notes key={lead.uuid} lead={lead} />

                                    <LeadInfo lead={lead} />

                                    {isLeadMessagesPageProps(props) && props.property && (
                                        <AmiBandsCard lead={lead} property={props.property} />
                                    )}

                                    <SmartFields lead={lead} />

                                    <LeadSidebarListings lead={lead} />

                                    <LeadStageTimeline lead={lead} stages={stages} interests={interests} teamMembers={teamMembers} isExpanded={isTimelineExpanded} />

                                    {isLeadMessagesPageProps(props) && !!props.attachments &&
                                        <MessageAttachments attachments={props.attachments} />
                                    }
                                </>)}
                                {tab === SidebarTab.TOURS && (<>
                                    <LeadSidebarDayCalendar
                                        key={lead.uuid}
                                        tours={showings}
                                        lead={lead}
                                        highlightedSlots={suggestedSlots}
                                        focusedPropertyId={focusedTourPropertyId}
                                        onSchedule={(startIso, propertyId) => {
                                            setScheduleInit({ startIso, propertyId });
                                            setFocusedTourPropertyId(propertyId);
                                            setIsTourModalOpen(true);
                                        }}
                                    />
                                    <UpcomingTour showings={showings} lead={lead} />
                                    <PastTours showings={showings} lead={lead} />
                                    <ToursFromOtherLeads showings={showings} lead={lead} />
                                </>)}
                                {tab === SidebarTab.PROPERTY &&
                                    <LeadSidebarPropertyTab
                                        leadPropertyId={lead.property_id}
                                        onInsertMessage={isLeadMessagesPageProps(props) ?
                                            props.insertIntoReplyInput :
                                            undefined}
                                    />
                                }
                                {isTourModalOpen && (
                                    <CreateOrUpdateTourModal
                                        open={isTourModalOpen}
                                        onClose={() => {
                                            setIsTourModalOpen(false);
                                            setScheduleInit(null);
                                            setFocusedTourPropertyId(undefined);
                                        }}
                                        leads={[lead]}
                                        selectedLeadUuid={lead.uuid}
                                        landlordUserId={lead.owner}
                                        initDateTime={scheduleInit?.startIso}
                                        initPropertyId={
                                            scheduleInit ?
                                                scheduleInit.propertyId :
                                                lead.property_id ?? null
                                        }
                                        onChanged={setFocusedTourPropertyId}
                                    />)}
                            </FlexColumn>
                        </FlexColumn>
                    </FlexColumn>
                </div>
            </Responsive>
        </ThemeProvider>
    );
}
